/**
 * Seed actionState.diffs for an exporter AP.
 *
 * Mirrors seedImportDiff.ts but uses dbValue = current value (not null),
 * fileValue = current value, isNew = false. This puts the Datenbank tab
 * into diff/edit mode where the user can tweak values before exporting.
 * The ExportPreviewView watches actionState.diffs and sends any changed
 * cells as overrides to the *-data endpoint for live preview.
 *
 * `scopeOrder` defaults to the customer-conformity walk; pass an explicit
 * order to seed a different exporter's downstream chain (e.g. supplier
 * → component → substance for the Lieferanten-Abfragen AP).
 */

import { api } from "../../../api/client"
import { useStore } from "../../../store/useStore"
import type {
  DiffCell,
  DiffRecord,
  PrimitiveType,
  ScopeDiffState,
  ScopeListItem,
} from "../../../types"

const CUSTOMER_CONFORMITY_SCOPE_ORDER = [
  "customer",
  "product",
  "component",
  "substance",
]

export async function seedExportDiff(
  anchorRecordId: string,
  scopeOrder: string[] = CUSTOMER_CONFORMITY_SCOPE_ORDER,
): Promise<void> {
  const store = useStore.getState()
  const { scopes } = store
  if (scopes.length === 0) return

  const ordered = scopes
    .filter((s) => scopeOrder.includes(s.id))
    .sort((a, b) => scopeOrder.indexOf(a.id) - scopeOrder.indexOf(b.id))

  let first = true
  for (const scopeItem of ordered) {
    const scopeDiff = await buildScopeDiff(scopeItem)
    if (!scopeDiff) continue

    if (first) {
      useStore.getState().enterDiffMode({
        scopeId: scopeDiff.scopeId,
        fileName: `Export-${anchorRecordId}`,
        records: scopeDiff.records,
        rawExtractions: [],
        anchoredRecords: [],
        exportMode: false,
      })
      first = false
    } else {
      useStore.getState().setActionScopeDiff(scopeItem.id, scopeDiff)
    }
  }
}

async function buildScopeDiff(
  scopeItem: ScopeListItem,
): Promise<ScopeDiffState | null> {
  let page
  try {
    page = await api.listRecords(scopeItem.id)
  } catch {
    return null
  }
  const fullScope = page.scope
  if (!fullScope || page.records.length === 0) return null

  const pkPrim = fullScope.primitives.find((p) => p.is_pk)
  const namePrim = fullScope.primitives.find(
    (p) => !p.is_pk && p.type === "string" && p.is_visible,
  )

  const diffRecords: Record<string, DiffRecord> = {}
  for (const rec of page.records) {
    const cells: Record<string, DiffCell> = {}
    for (const prim of fullScope.primitives) {
      if (prim.type === "relation" || prim.type === "formula") continue
      const pv = rec.values[prim.name]
      const raw = pv?.value ?? null
      cells[prim.id] = {
        recordId: rec.id,
        primitiveId: prim.id,
        primitiveName: prim.name,
        primitiveTitle: prim.title,
        primitiveType: prim.type as PrimitiveType,
        dbValue: raw,
        fileValue: raw,
        rawFileValue: raw,
        typeIncompatible: false,
        accepted: null,
      }
    }
    diffRecords[rec.id] = {
      recordId: rec.id,
      pkValue: pkPrim ? (rec.values[pkPrim.name]?.value ?? rec.id) : rec.id,
      nameValue: namePrim ? (rec.values[namePrim.name]?.value ?? null) : null,
      isNew: false,
      cells,
    }
  }

  if (Object.keys(diffRecords).length === 0) return null

  return {
    scopeId: scopeItem.id,
    fileName: `Export-${scopeItem.id}`,
    records: diffRecords,
    rawExtractions: [],
    anchoredRecords: [],
  }
}
